import {
  ExtendedMetadataAttributeFilterSummaryExternal,
  ExtendedMetadataDataType,
  SearchAssetFilter,
  SearchExtendedMetadataDateFilter,
  SearchExtendedMetadataStringFilter,
  SearchOrgFilter,
  SearchTaskFilter,
  SearchUserFilter,
} from '@askporter/client-grieg-lyric';

const typesThatNeedCounts: ExtendedMetadataDataType[] = [
  // only string & boolean types use checkbox or radios on the filters page, so these need counts
  ExtendedMetadataDataType.String,
  ExtendedMetadataDataType.Boolean,
];

/**
 * Converts the extended metadata array from a filter-values endpoint into a simple string list of atttributes that
 * should have counts
 *
 * @param extendedMetadataAttributeFilters - an array of ExtendedMetadataAttributeFilterSummaryExternal
 * @returns a string array of qualifiedJsonElementNames that can be sent with the POST search call
 */
export const getQualifiedStatNames = (
  extendedMetadataAttributeFilters: ExtendedMetadataAttributeFilterSummaryExternal[],
): string[] => {
  if (!extendedMetadataAttributeFilters) return [];

  const extendedMetadataStats = extendedMetadataAttributeFilters.reduce<string[]>(
    (prev, { dataType, qualifiedJsonElementName = '' }) => {
      if (typesThatNeedCounts.includes(dataType) && qualifiedJsonElementName)
        return [...prev, qualifiedJsonElementName];

      return prev;
    },
    [],
  );

  return extendedMetadataStats;
};

export type EntitySearchFilters = SearchTaskFilter | SearchAssetFilter | SearchUserFilter | SearchOrgFilter;

/**
 * When an extended metadata attribute has an active filter applied to itself, the *filtered* search
 * response's stats for that attribute are self-inclusive: every sibling value's count legitimately
 * comes back as 0, because the search has already been narrowed down to the selected value(s). Left
 * as-is, filter UIs that hide zero-count options (see `ExtendedMetadataFilters`) drop every sibling
 * option the moment one value is selected (NS-2544).
 *
 * This restores the *unfiltered* baseline stats for any attribute currently being filtered on, so
 * sibling options stay visible after a selection. Attributes with no active filter are left untouched.
 *
 * @param mergedStats - the stats produced by merging the filtered and unfiltered search results
 * @param unfilteredStats - the stats from the unfiltered ("all_stats") search request
 * @param extendedMetadataFilters - the currently active `extendedMetadata` filters, keyed by qualifiedJsonElementName
 * @returns a new stats object with self-filtered attributes restored to their unfiltered counts
 */
export function restoreUnfilteredStatsForActiveExtendedMetadataFilters<T extends Record<string, unknown>>(
  mergedStats: T,
  unfilteredStats: T,
  extendedMetadataFilters: EntitySearchFilters['extendedMetadata'],
): T {
  const restoredStats = { ...mergedStats };

  Object.keys(extendedMetadataFilters || {}).forEach((jsonElName) => {
    if (unfilteredStats?.[jsonElName] !== undefined) {
      restoredStats[jsonElName as keyof T] = unfilteredStats[jsonElName as keyof T];
    }
  });

  return restoredStats;
}

/**
 * Given an object of extended metadata filters, return a count of "applied filters" which can be used to determine
 * if filters are currently applied
 *
 * @param filters - some entity filter, either SearchTaskFilter, SearchAssetFilter, SearchUserFilter or SearchOrgFilter
 * @returns a number
 */
export const countAppliedExtendedMetadataFilters = (filters: EntitySearchFilters): number => {
  return Object.keys(filters?.extendedMetadata || {})?.reduce((acc, filterKey) => {
    try {
      const metadataValue = filters?.extendedMetadata?.[filterKey];
      if (!metadataValue) return acc;
      if ('in' in metadataValue) {
        const filter = metadataValue as SearchExtendedMetadataStringFilter;
        return acc + (filter.in?.length ?? 0);
      }

      if ('before' in metadataValue || 'after' in metadataValue) {
        const filter: SearchExtendedMetadataDateFilter = metadataValue;
        return acc + (filter.after || filter.before ? 1 : 0);
      }
      return acc;
    } catch (_err) {
      return acc;
    }

    return acc;
  }, 0);
};
